import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import type { DraftField, WatermarkDraft } from '../watermark-form';

import { StyleFields } from './StyleFields';

interface Props {
  draft: WatermarkDraft;
  errors: Partial<Record<DraftField, string>>;
  canEdit: boolean;
  canSave: boolean;
  saving: boolean;
  saveError: string | null;
  onChange: (patch: Partial<WatermarkDraft>) => void;
  onSave: () => void;
}

const LEGENDS = [
  { key: 'interna', label: 'Leyenda de copia interna', toggle: 'Copia interna activa' },
  { key: 'externa', label: 'Leyenda de copia externa', toggle: 'Copia externa activa' },
] as const;

/** Tarjeta "Configuración de marca de agua" del mockup: leyendas por tipo de copia, la
 *  línea que agrega el sistema, estilo y "solo vigentes". Sin permiso, solo lectura. */
export function WatermarkConfigCard({
  draft,
  errors,
  canEdit,
  canSave,
  saving,
  saveError,
  onChange,
  onSave,
}: Props): ReactElement {
  const disabled = !canEdit || saving;
  const toggle = (checked: boolean, label: string, onClick: () => void): ReactElement => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={cn('tgl', !checked && 'off')}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="sw" aria-hidden="true" />
      {label}
    </button>
  );

  return (
    <div className="card rise d1">
      <div className="ch">
        <h3>Configuración de marca de agua</h3>
        {!canEdit && <span className="count">solo lectura</span>}
      </div>
      <div className="cb form-grid">
        {LEGENDS.map((l) => (
          <div key={l.key} className="fld">
            <label className="fld">
              <span className="fld-lab">{l.label}</span>
              <input
                type="text"
                className="selector fw"
                maxLength={130}
                value={draft[l.key].text}
                disabled={disabled}
                aria-invalid={errors[l.key] !== undefined}
                onChange={(e) => onChange({ [l.key]: { ...draft[l.key], text: e.target.value } })}
              />
            </label>
            {errors[l.key] && <span className="fld-err">{errors[l.key]}</span>}
            {toggle(draft[l.key].enabled, l.toggle, () =>
              onChange({ [l.key]: { ...draft[l.key], enabled: !draft[l.key].enabled } }),
            )}
          </div>
        ))}
        <p className="wmnote">
          Debajo de la leyenda, cada copia lleva la empresa, quien la generó, la fecha y hora y un
          folio único.
        </p>
        <StyleFields draft={draft} errors={errors} disabled={disabled} onChange={onChange} />
        {toggle(draft.onlyCurrent, 'Solo documentos vigentes', () =>
          onChange({ onlyCurrent: !draft.onlyCurrent }),
        )}
        {saveError && (
          <p className="fld-err" role="alert">
            {saveError}
          </p>
        )}
        {canEdit && (
          <button
            type="button"
            className="btn wmsave"
            disabled={!canSave || saving}
            onClick={onSave}
          >
            {saving ? 'Guardando…' : 'Guardar configuración'}
          </button>
        )}
      </div>
    </div>
  );
}
